import { Suspense, useCallback, useEffect, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useFormatMessage } from "../Intl/Context";
import { useBottomSheetPanelContext } from "../Panels/BottomSheetPanel";
import { BoxSpinner } from "../Utils/Spinner";
import Button from "../Buttons/Button";
import SuggestionCardContent from "./SuggestionCardContent";

const SuggestionCard = ({
  contributionId,
  onNext,
  onPrevious,
  onComplete,
  isLast,
  isFirst,
  isPending
}) => {
  const t = useFormatMessage();
  const previousLabel = t(intlMessages.previous);
  const nextLabel = isLast ? t(intlMessages.finish) : t(intlMessages.next);

  const [isCommentFormOpen, setIsCommentFormOpen] = useState(false);

  const { setNavFooterContent } = useBottomSheetPanelContext();

  const cardClassName = classNames("suggestion-card", { loading: isPending });

  const handleNextClicked = useCallback(() => {
    if (isLast) onComplete();
    if (onNext) onNext();
  }, [onNext, onComplete, isLast]);

  // Keep the sheet footer in sync with the navigation buttons state. The nav
  // buttons are hidden while the comment form is open, and cleared when the
  // card unmounts.
  useEffect(() => {
    if (isCommentFormOpen) {
      setNavFooterContent(null);
      return;
    }

    setNavFooterContent(
      <div className="bottom-sheet-panel-actions">
        {!isFirst && (
          <Button
            type="button"
            className="btn btn-default btn-block-md bottom-sheet-panel-action"
            onClick={onPrevious}
            disabled={isPending}
            label={previousLabel}
            iconClassName="icon-arrow-left"
          />
        )}

        <Button
          type="button"
          className="btn btn-default btn-block-md bottom-sheet-panel-action text-primary"
          onClick={handleNextClicked}
          disabled={isPending}
          label={nextLabel}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />
      </div>
    );

    return () => setNavFooterContent(null);
  }, [
    isCommentFormOpen,
    isPending,
    isFirst,
    onPrevious,
    previousLabel,
    nextLabel,
    handleNextClicked,
    setNavFooterContent
  ]);

  return (
    <div className={cardClassName}>
      <div className="box padding-sm suggestion-card-content">
        <Suspense fallback={<BoxSpinner />}>
          <SuggestionCardContent
            contributionId={contributionId}
            onCommentFormOpenChange={setIsCommentFormOpen}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default SuggestionCard;

const intlMessages = defineMessages({
  previous: {
    id: "suggestionCard.previous",
    defaultMessage: "Précédent"
  },
  next: {
    id: "suggestionCard.next",
    defaultMessage: "Suivant"
  },
  finish: {
    id: "suggestionCard.finish",
    defaultMessage: "Terminer"
  }
});
